import React, { useContext, useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { IconTranslate, IconChevronDown } from "../../layout/SidebarIcons";
import { LANGUAGE_OPTIONS, getLanguageOption } from "./languageFlags";

import "./UserLanguageSelector.css";

function LanguageFlag({ src, alt, className = "lang-selector__flag" }) {
	return <img src={src} alt={alt} className={className} width={18} height={18} />;
}

function UserLanguageSelector({ variant, darkMode, className = "" }) {
	const [open, setOpen] = useState(false);
	const [dropdownRect, setDropdownRect] = useState(null);
	const { user } = useContext(AuthContext);
	const containerRef = useRef(null);
	const buttonRef = useRef(null);

	const currentLang = user?.language || i18n.language || "pt-BR";
	const currentOption = getLanguageOption(currentLang);
	const isSidebar = variant === "sidebar";
	const isTopbar = variant === "topbar";

	const handleToggle = (e) => {
		e.stopPropagation();
		if (!open && isSidebar && buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect();
			setDropdownRect({
				top: rect.top + rect.height / 2,
				left: rect.right + 8,
			});
		}
		setOpen((prev) => !prev);
	};

	const handleChangeLanguage = async (language) => {
		try {
			await i18n.changeLanguage(language);
			if (user?.id) await api.put(`/users/${user.id}`, { language });
		} catch (err) {
			toastError(err);
		}
		setOpen(false);
		setDropdownRect(null);
	};

	useEffect(() => {
		if (!open) return;
		const close = (e) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target) &&
				!e.target.closest(".lang-selector__dropdown")
			) {
				setOpen(false);
				setDropdownRect(null);
			}
		};
		document.addEventListener("click", close);
		return () => document.removeEventListener("click", close);
	}, [open]);

	const darkClass = darkMode ? "lang-selector--dark" : "";
	const sidebarClass = isSidebar ? "lang-selector--sidebar" : "";
	const topbarClass = isTopbar ? "lang-selector--topbar" : "";

	const dropdownList = (
		<ul
			className={`lang-selector__dropdown ${darkClass} ${sidebarClass} ${topbarClass} ${isSidebar ? "lang-selector__dropdown--portal" : ""}`}
			role="menu"
			style={
				isSidebar && dropdownRect
					? {
							position: "fixed",
							top: dropdownRect.top,
							left: dropdownRect.left,
							transform: "translateY(-50%)",
							margin: 0,
							zIndex: 99999,
					  }
					: undefined
			}
		>
			{LANGUAGE_OPTIONS.map(({ code, labelKey, flag, flagAlt }) => (
				<li key={code} role="none">
					<button
						type="button"
						role="menuitem"
						className={`lang-selector__dropdown-item ${currentLang === code ? "lang-selector__dropdown-item--active" : ""}`}
						onClick={() => handleChangeLanguage(code)}
					>
						<LanguageFlag src={flag} alt={flagAlt} className="lang-selector__flag lang-selector__flag--menu" />
						<span>{i18n.t(labelKey)}</span>
					</button>
				</li>
			))}
		</ul>
	);

	return (
		<div
			ref={containerRef}
			className={`lang-selector ${darkClass} ${sidebarClass} ${topbarClass} ${className}`.trim()}
		>
			<button
				ref={buttonRef}
				type="button"
				className={`lang-selector__btn ${isSidebar ? "lang-selector__btn--icon-only" : ""} ${isTopbar ? "lang-selector__btn--topbar" : ""}`}
				onClick={handleToggle}
				aria-haspopup="true"
				aria-expanded={open}
				title={i18n.t(currentOption.labelKey)}
			>
				{isTopbar || isSidebar ? (
					<LanguageFlag
						src={currentOption.flag}
						alt={currentOption.flagAlt}
						className={isTopbar ? "lang-selector__flag lang-selector__flag--trigger" : "lang-selector__flag"}
					/>
				) : (
					<>
						<LanguageFlag src={currentOption.flag} alt={currentOption.flagAlt} />
						<IconTranslate style={{ width: 18, height: 18, opacity: 0.85 }} />
					</>
				)}
				{!isSidebar && !isTopbar && (
					<span className="lang-selector__label">
						{i18n.t(currentOption.labelKey)}
					</span>
				)}
				{!isTopbar && (
					<span className="lang-selector__chevron" aria-hidden>
						<IconChevronDown style={{ width: 18, height: 18 }} />
					</span>
				)}
			</button>

			{isSidebar
				? open && dropdownRect && ReactDOM.createPortal(dropdownList, document.body)
				: open && dropdownList}
		</div>
	);
}

export default UserLanguageSelector;
